import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import { usePropertyId } from '../../common/hooks/usePropertyId';
import { useRoomsData } from '../../common/hooks/useRoomsData';
import { useTennisCourtReservationsData } from '../../common/hooks/useTennisCourtReservationsData';
import { withMultipleQueryData } from '../../common/withQueryData';
import SiteFooter from './components/SiteFooter';

const OPENING_HOUR = 8;
const CLOSING_HOUR = 22;
const SLOT_MINUTES = 30;
const MAX_DURATION_MINUTES = 180;
const BOOKABLE_DAYS = 7;

const toDateString = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatDateLabel = (dateString) => {
  const date = dateString instanceof Date ? dateString : new Date(`${dateString}T12:00:00`);
  return date.toLocaleDateString('pl-PL', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  });
};

const formatTimeLabel = (time) => (typeof time === 'string' ? time.slice(0, 5) : time);

const buildHalfHourSlots = () => {
  const slots = [];
  for (let minutes = OPENING_HOUR * 60; minutes < CLOSING_HOUR * 60; minutes += SLOT_MINUTES) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
  return slots;
};

const ALL_START_SLOTS = buildHalfHourSlots();

const durationOptions = Array.from(
  { length: MAX_DURATION_MINUTES / SLOT_MINUTES },
  (_, i) => (i + 1) * SLOT_MINUTES
);

const addMinutes = (time, minutesToAdd) => {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + minutesToAdd;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const overlaps = (startA, endA, startB, endB) => startA < endB && endA > startB;

const bookableDates = () => {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Array.from({ length: BOOKABLE_DAYS }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    return toDateString(d);
  });
};

const normalizeReservationDate = (value) => {
  if (value instanceof Date) {
    return toDateString(value);
  }
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }
  return value;
};

/**
 * Schedule + booking form for the property tennis court.
 * Expects rooms and reservations from withMultipleQueryData.
 */
const TennisCourtPageContent = ({ queryResults, reloadAll, createReservation }) => {
  const rooms = queryResults.rooms?.data || [];
  const reservationsData = queryResults.reservations?.data;

  const dates = useMemo(() => bookableDates(), []);
  const [selectedDate, setSelectedDate] = useState(dates[0]);
  const [roomId, setRoomId] = useState('');
  const [startTime, setStartTime] = useState('');
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const dayReservations = useMemo(
    () =>
      (reservationsData || [])
        .filter((r) => normalizeReservationDate(r.reservationDate) === selectedDate)
        .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime))),
    [reservationsData, selectedDate]
  );

  const availableStartSlots = useMemo(() => {
    const now = new Date();
    const todayStr = toDateString(now);
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    return ALL_START_SLOTS.filter((slot) => {
      const end = addMinutes(slot, durationMinutes);
      if (end > `${String(CLOSING_HOUR).padStart(2, '0')}:00`) {
        return false;
      }
      if (selectedDate === todayStr) {
        const [h, m] = slot.split(':').map(Number);
        if (h * 60 + m <= currentMinutes) {
          return false;
        }
      }
      return !dayReservations.some((r) =>
        overlaps(
          slot,
          end,
          formatTimeLabel(r.startTime),
          formatTimeLabel(r.endTime)
        )
      );
    });
  }, [dayReservations, durationMinutes, selectedDate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    if (!roomId) {
      setFormError('Wybierz pokój');
      return;
    }
    if (!startTime) {
      setFormError('Wybierz godzinę rozpoczęcia');
      return;
    }

    setSubmitting(true);
    try {
      await createReservation({
        roomId,
        reservationDate: selectedDate,
        startTime,
        durationMinutes,
      });
      setSuccessMessage('Kort zarezerwowany');
      setStartTime('');
      await reloadAll();
    } catch (error) {
      const message =
        error?.cause?.response?.data?.message ||
        error?.message ||
        'Nie udało się zarezerwować kortu';
      setFormError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f7f5f2' }}>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1f4d3a 0%, #2d6a4f 55%, #40916c 100%)',
          color: '#fff',
          py: { xs: 5, md: 7 },
        }}
      >
        <Container maxWidth="md">
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
            <SportsTennisIcon sx={{ fontSize: 36 }} />
            <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.9 }}>
              Udogodnienia
            </Typography>
          </Stack>
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, fontFamily: '"Georgia", "Times New Roman", serif' }}
          >
            Rezerwacja kortu tenisowego
          </Typography>
          <Typography sx={{ mt: 1.5, maxWidth: 560, opacity: 0.92 }}>
            Wybierz swój pokój, dzień i termin z dokładnością do 30 minut.
            Rezerwacje dostępne na dziś i kolejne 6 dni.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Stack spacing={4}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Harmonogram
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              sx={{ overflowX: 'auto', pb: 1, mb: 2 }}
            >
              {dates.map((date) => (
                <Button
                  key={date}
                  variant={selectedDate === date ? 'contained' : 'outlined'}
                  onClick={() => {
                    setSelectedDate(date);
                    setStartTime('');
                  }}
                  sx={{
                    minWidth: 110,
                    flexShrink: 0,
                    textTransform: 'none',
                    bgcolor: selectedDate === date ? '#2d6a4f' : 'transparent',
                    borderColor: '#2d6a4f',
                    color: selectedDate === date ? '#fff' : '#2d6a4f',
                    '&:hover': {
                      bgcolor: selectedDate === date ? '#1f4d3a' : 'rgba(45,106,79,0.08)',
                      borderColor: '#2d6a4f',
                    },
                  }}
                >
                  {formatDateLabel(date)}
                </Button>
              ))}
            </Stack>

            {dayReservations.length === 0 ? (
              <Alert severity="info">Brak rezerwacji w wybranym dniu — kort jest wolny.</Alert>
            ) : (
              <Stack spacing={1}>
                {dayReservations.map((reservation) => (
                  <Box
                    key={reservation.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 2,
                      px: 2,
                      py: 1.5,
                      bgcolor: '#fff',
                      borderLeft: '4px solid #2d6a4f',
                    }}
                  >
                    <Typography sx={{ fontWeight: 600 }}>
                      {formatTimeLabel(reservation.startTime)} – {formatTimeLabel(reservation.endTime)}
                    </Typography>
                    <Typography color="text.secondary">
                      {reservation.roomName || reservation.roomId}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              bgcolor: '#fff',
              p: { xs: 2.5, md: 3.5 },
              borderTop: '3px solid #2d6a4f',
            }}
          >
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 600 }}>
              Nowa rezerwacja
            </Typography>

            <Stack spacing={2.5}>
              <FormControl fullWidth required>
                <InputLabel id="tennis-room-label">Pokój</InputLabel>
                <Select
                  labelId="tennis-room-label"
                  label="Pokój"
                  value={roomId}
                  onChange={(e) => setRoomId(e.target.value)}
                >
                  {rooms.map((room) => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Rezerwacja jest powiązana z pokojem gościa</FormHelperText>
              </FormControl>

              <FormControl fullWidth required>
                <InputLabel id="tennis-duration-label">Czas trwania</InputLabel>
                <Select
                  labelId="tennis-duration-label"
                  label="Czas trwania"
                  value={durationMinutes}
                  onChange={(e) => {
                    setDurationMinutes(Number(e.target.value));
                    setStartTime('');
                  }}
                >
                  {durationOptions.map((minutes) => {
                    let label;
                    if (minutes < 60) {
                      label = `${minutes} min`;
                    } else if (minutes % 60 === 0) {
                      label = minutes === 60 ? '1 godzina' : `${minutes / 60} godz.`;
                    } else {
                      label = `${Math.floor(minutes / 60)},5 godz.`;
                    }
                    return (
                      <MenuItem key={minutes} value={minutes}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl fullWidth required error={availableStartSlots.length === 0}>
                <InputLabel id="tennis-start-label">Godzina rozpoczęcia</InputLabel>
                <Select
                  labelId="tennis-start-label"
                  label="Godzina rozpoczęcia"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  disabled={availableStartSlots.length === 0}
                >
                  {availableStartSlots.map((slot) => (
                    <MenuItem key={slot} value={slot}>
                      {slot} – {addMinutes(slot, durationMinutes)}
                    </MenuItem>
                  ))}
                </Select>
                {availableStartSlots.length === 0 && (
                  <FormHelperText>Brak wolnych terminów dla wybranego czasu trwania</FormHelperText>
                )}
              </FormControl>

              {formError && <Alert severity="error">{formError}</Alert>}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={submitting || availableStartSlots.length === 0}
                sx={{
                  alignSelf: 'flex-start',
                  bgcolor: '#2d6a4f',
                  '&:hover': { bgcolor: '#1f4d3a' },
                  textTransform: 'none',
                  px: 4,
                }}
              >
                {submitting ? 'Rezerwuję…' : 'Zarezerwuj kort'}
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Container>

      <SiteFooter />

      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage('')}
        message={successMessage}
      />
    </Box>
  );
};

const TennisCourtPageContentWithData = withMultipleQueryData(TennisCourtPageContent);

const TennisCourtPage = () => {
  const propertyId = usePropertyId();
  const { queryResults: roomsQuery } = useRoomsData(propertyId);
  const { queryResults: reservationsQuery, createReservation } =
    useTennisCourtReservationsData(propertyId);

  return (
    <TennisCourtPageContentWithData
      queryResults={{
        rooms: roomsQuery,
        reservations: reservationsQuery,
      }}
      createReservation={createReservation}
    />
  );
};

export default TennisCourtPage;
